import { z } from 'zod';

/* Contratos do catálogo de packs publicados no registry (auditoria 25/06, ADR-0400).
   O admin-service expõe `GET /v1/admin/packs` (read-only), que busca o catálogo global
   do pack-registry via HMAC. Distinto da fila de moderação (pack-submissions): aqui é
   o que JÁ está publicado/live (first-party + community live + org-private). O shape
   espelha o `CatalogPack` do registry (lib/internal-catalog-route.ts). */

export const packTierSchema = z.enum(['free', 'pro', 'team', 'enterprise']);
export type PackTier = z.infer<typeof packTierSchema>;

export const packVisibilitySchema = z.enum(['public', 'org-private', 'byMyself']);
export type PackVisibility = z.infer<typeof packVisibilitySchema>;

export const packKindSchema = z.enum(['template', 'extension', 'module-pack']);
export type PackKind = z.infer<typeof packKindSchema>;

export const publishedPackDto = z.object({
  id: z.string(),
  displayName: z.string(),
  author: z.string(),
  verified: z.boolean(),
  tier: packTierSchema,
  visibility: packVisibilitySchema,
  /* Orgs autorizadas quando `visibility === 'org-private'` (recorte por org da aba
     Companies, ADR-0400+). Ausente nos demais. */
  allowedOrgs: z.array(z.string()).optional(),
  kind: packKindSchema,
  /* Versão LIVE (version-index). `null` = todas as versões yanked/deprecated. */
  latestActive: z.string().nullable(),
  versionCount: z.number().int(),
  installs: z.number().int(),
  lastUpdated: z.string(),
});
export type PublishedPack = z.infer<typeof publishedPackDto>;

/* Paginação por cursor keyset do catálogo (espelha `listCompaniesQuery`, ADR-0347): `limit`
   default 50 / teto 100; `cursor` opaco encadeia as páginas. A ordenação keyset estável vive
   no pack-registry `(lastUpdated desc, id desc)`; os filtros da UI (visibility/tier/kind/
   verified/ordenação) seguem client-side sobre as páginas já carregadas. */
export const listPacksQuery = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListPacksQuery = z.infer<typeof listPacksQuery>;

/* Página do catálogo `{ items, nextCursor }` (mesma forma do `cursorPageSchema` do admin-web).
   `nextCursor` null/ausente = última página. O admin-service valida esta forma vinda do
   registry — drift de contrato vira 502, não 200 corrompido. */
export const publishedPacksPageDto = z.object({
  items: z.array(publishedPackDto),
  nextCursor: z.string().nullish(),
});
export type PublishedPacksPage = z.infer<typeof publishedPacksPageDto>;

/* Takedown de versão por staff (TASK-PUB-13 / ADR-0464). `reason` obrigatório (auditoria de
   enforcement). Response: `latestActive` recomputado (a versão revogada sai do candidato). */
export const takedownRequestDto = z.object({
  reason: z.string().min(1).max(2000),
});
export type TakedownRequest = z.infer<typeof takedownRequestDto>;

export const takedownResponseDto = z.object({
  ok: z.literal(true),
  latestActive: z.string().nullable(),
});
export type TakedownResponse = z.infer<typeof takedownResponseDto>;

/* Takedown homogêneo em lote (ADR-0829). O alvo inclui a versão congelada na confirmação; usar
   somente `packId` e resolver `latestActive` depois permitiria atingir uma publicação concorrente.
   Teto menor porque cada item é uma chamada privilegiada cross-service ao registry. */
export const PACK_TAKEDOWN_BULK_MAX_TARGETS = 10;
export const bulkPackTakedownTargetDto = z.object({
  packId: z.string().min(1),
  version: z.string().min(1),
});
export type BulkPackTakedownTarget = z.infer<typeof bulkPackTakedownTargetDto>;

export const bulkPackTakedownBody = z.object({
  targets: z
    .array(bulkPackTakedownTargetDto)
    .min(1)
    .max(PACK_TAKEDOWN_BULK_MAX_TARGETS)
    .refine(
      (targets) =>
        new Set(targets.map((target) => `${target.packId}\u0000${target.version}`)).size ===
        targets.length,
      { message: 'duplicate_bulk_targets' },
    ),
  reason: z.string().trim().min(1).max(2000),
});
export type BulkPackTakedownBody = z.infer<typeof bulkPackTakedownBody>;

export const bulkPackTakedownResponseDto = z.object({
  results: z.array(
    z.object({
      packId: z.string(),
      version: z.string(),
      ok: z.boolean(),
      latestActive: z.string().nullable().optional(),
      error: z.string().nullish(),
    }),
  ),
});
export type BulkPackTakedownResponse = z.infer<typeof bulkPackTakedownResponseDto>;

/* Detalhe rico de UM pack publicado (Fase 2, ADR-0703+). O admin-service expõe
   `GET /v1/admin/packs/:packId` (read-only), que lê `GET /v1/internal/packs/:packId`
   do pack-registry via HMAC. Distinto do `publishedPackDto` (item da lista): traz a
   metadata rica (description/capabilities/tags/category/minAppVersion/publisherId) e a
   lista completa de versões com o status de ciclo de vida do version-index. */

/* Status de ciclo de vida da versão (version-index do registry). `revoked` = takedown de
   STAFF (enforcement, ADR-0464); `yanked`/`deprecated` = lifecycle do publisher. */
export const packVersionStatusSchema = z.enum(['active', 'yanked', 'deprecated', 'revoked']);
export type PackVersionStatus = z.infer<typeof packVersionStatusSchema>;

export const packVersionDto = z.object({
  version: z.string(),
  status: packVersionStatusSchema,
  publishedAt: z.string(),
  /* publisherId (community) ou `null` (first-party). */
  publishedBy: z.string().nullable(),
  /* Versão que substitui esta quando `deprecated` (ADR-0369). Ausente nos demais. */
  replacedByVersion: z.string().optional(),
  /* Notas de release, públicas por design (ADR-0468). */
  releaseNotes: z.string().optional(),
  /* Metadados de takedown de staff (ADR-0464) — presentes só em versões `revoked`. Visão de
     staff (endpoint HMAC); é a fonte per-pack da trilha de enforcement (o audit-log grava
     `pack.takedown` com `targetId=version`, não packId, então não filtra por pack — ADR-0464). */
  takedownAt: z.string().optional(),
  takedownBy: z.string().optional(),
  takedownReason: z.string().optional(),
});
export type PackVersion = z.infer<typeof packVersionDto>;

export const packDetailDto = z.object({
  id: z.string(),
  /* Registro de publisher dono (ADR-0528). Ausente em first-party/pré-migração. */
  publisherId: z.string().optional(),
  displayName: z.string(),
  author: z.string(),
  verified: z.boolean(),
  description: z.string(),
  tier: packTierSchema,
  visibility: packVisibilitySchema,
  /* Orgs autorizadas quando `visibility === 'org-private'`. Ausente nos demais. */
  allowedOrgs: z.array(z.string()).optional(),
  kind: packKindSchema,
  /* Taxonomia do marketplace (29-08). Ausente = sem categoria. */
  category: z.string().optional(),
  /* Tags de descoberta (29-08). Ausente/vazio = sem tags. */
  tags: z.array(z.string()).optional(),
  capabilities: z.array(z.string()),
  minAppVersion: z.string(),
  /* Versão LIVE (version-index). `null` = todas yanked/deprecated/revoked. */
  latestActive: z.string().nullable(),
  versionCount: z.number().int(),
  installs: z.number().int(),
  rating: z.number(),
  lastUpdated: z.string(),
  /* Histórico completo de versões (desc por semver no registry) com status. */
  versions: z.array(packVersionDto),
});
export type PackDetail = z.infer<typeof packDetailDto>;
